import { computeOrderTotals } from "@/features/shared";
import { AddProductVariantSection, CenteredLoading } from "@/features/ui";
import { yupResolver } from "@hookform/resolvers/yup";
import { Divider, Paper } from "@mui/material";
import Grid from "@mui/material/Grid";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { GeneralInformationSection } from "../sales-order-form/general-information-section";
import { LensProductSection } from "../sales-order-form/lens-product-section";
import { OtherSection } from "../sales-order-form/other-section";
import { PriceSummarySection } from "../sales-order-form/price-summary-section";
import { GeneralProductSection } from "../sales-order-manual-form/general-product-section";
import {
  DEFAULT_VALUES,
  VALIDATION_SCHEMA,
} from "./edit-sales-order-form-constants";

export const EditSalesOrderForm = ({
  defaultValues: providedDefaultValues,
  isLoading = false,
  onSubmit,
  renderActionButton,
}) => {
  const methods = useForm({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });

  const { watch, setValue, handleSubmit, formState, reset } = methods;

  // Reset form when providedDefaultValues changes (after data loads)
  useEffect(() => {
    if (providedDefaultValues) {
      reset({ ...DEFAULT_VALUES, ...providedDefaultValues });
    }
  }, [providedDefaultValues, reset]);

  const salesOrderProductVariants = watch("salesOrderProductVariants");
  const salesOrderLensProducts = watch("salesOrderLensProducts");
  const seller = watch("seller");

  // Calculate subtotal from product variants and lens products
  useEffect(() => {
    const salesOrderProductVariantTotalPrice =
      salesOrderProductVariants?.reduce((acc, variant) => {
        const quantity = variant?.quantity || 0;
        const costPerUnit = variant?.costPerUnit || 0;
        const discount = variant?.discount || 0;
        return acc + (quantity * costPerUnit - discount);
      }, 0) || 0;

    const salesOrderLensProductTotalPrice =
      salesOrderLensProducts?.reduce((acc, lensProduct) => {
        const price = lensProduct?.price || 0;
        return acc + price;
      }, 0) || 0;

    setValue(
      "subtotal",
      salesOrderProductVariantTotalPrice + salesOrderLensProductTotalPrice
    );
  }, [salesOrderProductVariants, salesOrderLensProducts, setValue]);

  const onFormSubmit = async (formData) => {
    const { taxAmount, grandTotal } = computeOrderTotals(formData);

    const payload = {
      taxable: formData.taxable,
      totalIncludesTax: formData.taxable && formData.totalIncludesTax,
      taxPercentage: formData.taxPercentage,
      taxAmount,
      transactionDate: dayjs(formData.transactionDate).format("YYYY-MM-DD"),
      paymentTermId: formData.paymentTerm?.id,
      generalProductVariants: (formData.salesOrderProductVariants || []).map(
        (variant) => {
          const payload = {
            sku: variant.sku,
            quantity: variant.quantity,
            discount: variant.discount || 0,
            note: variant.note || "",
          };

          // Only include id for existing products (not newly added ones)
          if (!variant.isNewlyAdded) {
            payload.id = variant.id;
          }

          return payload;
        }
      ),
      lensProducts: (formData.salesOrderLensProducts || []).map(
        (lensProduct) => {
          // Get productId from first variant
          const productId =
            lensProduct.salesRequestProductVariants?.[0]
              ?.purchaseOrderProductVariant?.productId;

          return {
            id: lensProduct.id,
            productId: productId,
            discount: lensProduct.discount || 0,
            lensProductVariants: (
              lensProduct.salesRequestProductVariants || []
            ).map((srpv) => ({
              id: srpv.id,
              side: srpv.purchaseOrderProductVariant?.side,
              sku: srpv.purchaseOrderProductVariant?.sku,
              note: srpv.purchaseOrderProductVariant?.note || "",
            })),
            productAddOns: (lensProduct.productAddOns || []).map((addon) => ({
              productAddOnId: addon.productAddOnId,
            })),
            note: lensProduct.note || "",
          };
        }
      ),
      grandTotal,
      discountType: formData.discountType,
      discountAmount: formData.discountInput || 0,
      note: formData.note || "",
    };

    return await onSubmit(payload);
  };

  // Show loading spinner while fetching data
  if (isLoading) {
    return <CenteredLoading />;
  }

  return (
    <FormProvider {...methods}>
      <Grid
        container
        component="form"
        noValidate
        onSubmit={handleSubmit(onFormSubmit)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
          }
        }}
        spacing={3}
      >
        <Grid size={{ xs: 12 }}>
          <GeneralInformationSection disableBuyerSeller={true} />
        </Grid>

        {/* General Products Section - Bulk Edit Grid */}
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 2.5, borderRadius: 2 }}>
            <AddProductVariantSection
              outletId={seller?.id}
              onAdded={(productVariant, product, quantity) => {
                const currentVariants =
                  watch("salesOrderProductVariants") || [];
                setValue("salesOrderProductVariants", [
                  ...currentVariants,
                  {
                    id: productVariant.id, // Use actual variant ID for new products
                    productVariantId: productVariant.id,
                    name: productVariant.name,
                    sku: productVariant.sku,
                    unitOfMeasurement: product.unitOfMeasurement.name,
                    costPerUnit: productVariant.distributorPrice,
                    discount: 0,
                    quantity,
                    deliveredQuantity: 0,
                    invoicedQuantity: 0,
                    maxQuantity: null, // No max for manually added products
                    minQuantity: 0,
                    isNewlyAdded: true, // Flag to identify new products
                  },
                ]);
              }}
            />
            <Divider sx={{ my: 2.5 }} />
            <GeneralProductSection editMode={true} />
          </Paper>
        </Grid>

        {/* Lens Products Section - Separate */}
        {salesOrderLensProducts && salesOrderLensProducts.length > 0 && (
          <Grid size={{ xs: 12 }}>
            <LensProductSection editMode={true} />
          </Grid>
        )}

        <Grid size={{ xs: 12, md: 6 }}>
          <OtherSection />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <PriceSummarySection />
        </Grid>
        <Grid
          size={{ xs: 12 }}
          sx={{
            display: "flex",
            justifyContent: "flex-end"
          }}>
          {renderActionButton({ isSubmitting: formState.isSubmitting })}
        </Grid>
      </Grid>
    </FormProvider>
  );
};

EditSalesOrderForm.propTypes = {
  defaultValues: PropTypes.object,
  isLoading: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  renderActionButton: PropTypes.func.isRequired,
};
